import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { resetAllChatsStreaming, setIsNewChat, setLastActiveChatId, setShowChatbox } from '../../redux/slices/chat';
import { ChatPrefill, setChatPrefill } from '../GlobalChatBox/prefill';
import { getDashboardStatusColor, getDashboardStatusLabel } from '../../constants/integrationTypes';
import { HiSparkles } from 'react-icons/hi';
import { pluralizeEntityType } from '../../utils/entityDisplayUtils';

interface RiskDetail {
  type: 'unallocated' | 'delayed' | 'blocked' | 'at_risk';
  count: number;
  entityType: string;
}

interface RiskStatusDropdownProps {
  entityName: string;
  entityType: 'strategy_map' | 'work_unit' | 'resource' | 'team';
  entityId: string;
  riskDetails: RiskDetail[];
  status?: string;
  className?: string;
  children: React.ReactNode;
}

const RiskStatusDropdown: React.FC<RiskStatusDropdownProps> = ({
  entityName,
  entityType,
  entityId,
  riskDetails,
  status,
  className = '',
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 320;
      const dropdownHeight = 200;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 16;

      // Try to center dropdown on trigger
      let left = triggerRect.left + (triggerRect.width / 2) - (dropdownWidth / 2);

      // Ensure dropdown doesn't go off-screen left
      if (left < padding) {
        left = padding;
      }
      // Ensure dropdown doesn't go off-screen right
      if (left + dropdownWidth > viewportWidth - padding) {
        left = viewportWidth - dropdownWidth - padding;
      }

      // Calculate vertical position - prefer below, fallback to above
      let top = triggerRect.bottom + 8;
      if (top + dropdownHeight > viewportHeight - padding) {
        top = triggerRect.top - dropdownHeight - 8;
      }
      // Ensure dropdown doesn't go off-screen top
      if (top < padding) {
        top = padding;
      }

      setDropdownPosition({ top, left });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setIsVisible(false);
        setTimeout(() => setIsOpen(false), 1000);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsVisible(false);
        setTimeout(() => setIsOpen(false), 1000);
      }
    };

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const calculateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const dropdownHeight = 200;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16; // Minimum padding from viewport edge

    // Try to center dropdown on trigger
    let left = triggerRect.left + (triggerRect.width / 2) - (dropdownWidth / 2);

    // Ensure dropdown doesn't go off-screen left
    if (left < padding) {
      left = padding;
    }
    // Ensure dropdown doesn't go off-screen right
    if (left + dropdownWidth > viewportWidth - padding) {
      left = viewportWidth - dropdownWidth - padding;
    }

    // Calculate vertical position - prefer below, fallback to above
    let top = triggerRect.bottom + 8;
    if (top + dropdownHeight > viewportHeight - padding) {
      top = triggerRect.top - dropdownHeight - 8;
    }
    // Ensure dropdown doesn't go off-screen top
    if (top < padding) {
      top = padding;
    }

    return { top, left };
  }, []);

  const formatRiskText = () => {
    const reasons: string[] = [];

    riskDetails.forEach(detail => {
      const displayType = detail.entityType === 'project' ? 'work unit' : detail.entityType;
      const entityLabel = pluralizeEntityType(detail.count, displayType);
      
      if (detail.type === 'at_risk') {
        reasons.push(`${detail.count} ${entityLabel} ${detail.count === 1 ? 'is' : 'are'} at risk`);
      } else if (detail.type === 'blocked') {
        reasons.push(`${detail.count} ${entityLabel} ${detail.count === 1 ? 'is' : 'are'} blocked`);
      } else if (detail.type === 'unallocated') {
        reasons.push(`${detail.count} ${entityLabel} need${detail.count === 1 ? 's' : ''} allocation`);
      } else if (detail.type === 'delayed') {
        reasons.push(`${detail.count} ${entityLabel} ${detail.count === 1 ? 'is' : 'are'} delayed`);
      }
    });

    return reasons.join(', and ');
  };

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openDropdown = useCallback(() => {
    cancelScheduledClose();
    setDropdownPosition(calculateDropdownPosition());
    setIsOpen(true);
    setTimeout(() => setIsVisible(true), 10);
  }, [calculateDropdownPosition, cancelScheduledClose]);

  const scheduleCloseDropdown = useCallback(() => {
    setIsVisible(false);
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, 200);
  }, [cancelScheduledClose]);

  const handleMouseEnter = () => {
    openDropdown();
  };

  const handleMouseLeave = () => {
    scheduleCloseDropdown();
  };

  // Programmatic open/close support keyed by entityId
  useEffect(() => {
    const onOpen = (e: any) => {
      if (e?.detail?.id && String(e.detail.id) === String(entityId)) {
        openDropdown();
      }
    };
    const onClose = (e: any) => {
      if (!e?.detail?.id || String(e.detail.id) === String(entityId)) {
        scheduleCloseDropdown();
      }
    };
    window.addEventListener('cadence:risk-open', onOpen);
    window.addEventListener('cadence:risk-close', onClose);
    return () => {
      window.removeEventListener('cadence:risk-open', onOpen);
      window.removeEventListener('cadence:risk-close', onClose);
    };
  }, [entityId, openDropdown, scheduleCloseDropdown]);

  const handleSuggestionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const prompt = `Analyse why @${entityName} is at risk? Provide actionable recommendations to mitigate the risk.`;

    try {
      setChatPrefill({
        type: entityType,
        id: String(entityId),
        name: entityName,
        fullPrompt: prompt,
        source: 'risk_mitigation' as any
      } as ChatPrefill);

      dispatch(resetAllChatsStreaming());
      dispatch(setIsNewChat(true));
      dispatch(setLastActiveChatId(null));
      dispatch(setShowChatbox(true));

      const evt = new CustomEvent('cadence:chat-prefill', {
        detail: {
          type: entityType,
          id: String(entityId),
          name: entityName,
          fullPrompt: prompt,
          source: 'risk_mitigation'
        }
      });
      window.dispatchEvent(evt);
    } catch (error) {
      console.error('Error setting chat prefill:', error);
    }

    scheduleCloseDropdown();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Element */}
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={e => e.stopPropagation()}
        className="cursor-pointer"
      >
        {children}
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={e => e.stopPropagation()}
          className={`fixed z-[9999] w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200 ease-out transform mt-[-5px] ${isVisible
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-1 scale-95'
            }`}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/30">
            <div className="flex items-center gap-2">
              <HiExclamationTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">
                Risk Analysis
              </h3>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                <span className="text-black font-bold dark:text-white">{entityName}</span> is  <span className={`inline-flex items-center px-1.5 py-0.5 mx-1 rounded-lg text-white text-xs font-medium ${getDashboardStatusColor(status || '')}`}>
                  {getDashboardStatusLabel(status || '')}
                </span> <br></br>{formatRiskText()}. Want recommendations to get back on track?
              </p>
            </div>

            <div className="pt-2 border-t flex justify-center border-gray-100 dark:border-gray-700">
              <button
                data-onboarding="risk-analyze-button"
                onClick={handleSuggestionClick}
                className="w-full flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors duration-150"
              >
                <HiSparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span>Analyse Deeper</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RiskStatusDropdown;
